import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AccessGroup } from '@/api/auth';
import type { Role } from '@/api/vacancies';
import { Button } from '@/components/ui/Button';
import { DropdownMenu, type MenuItem } from '@/components/ui/DropdownMenu';

import { CloseVacancyDialog } from './CloseVacancyDialog';
import { isImportedVacancy } from './requisition';
import { mayWriteVacancies } from './vacancyAccess';
import { VacancyDialog } from './VacancyDialog';
import { VacancySetsDialog } from './VacancySetsDialog';

interface Props {
  role: Role;
  groups: AccessGroup[];
  /** The session user's id — `VacancySetsDialog` reads a list that is narrowed per caller. */
  viewerId: number;
}

/**
 * §6.8's row menu: **Edit**, **Question sets**, and **Close** or **Reopen** depending on which the
 * vacancy is — ⚠️ **and on an ADP-sourced vacancy it is *Question sets* alone** (Sprint 24; see the
 * two notes in the body).
 *
 * ⚠️ **There is no *Delete* and there will not be one** (ADR 0005, **feedback protection #2**). §6.9's
 * row of §7 predicts *"Add a Delete action to the row menu"* for the question bank, and this menu is
 * where the same edit would be attempted for a vacancy — a *Delete vacancy* item is the single most
 * obvious thing a designer would draw here. The answer is **Close**: `PATCH {"is_open": false}`, after
 * which the row, its questions, its candidates and every score recorded against them are still there.
 * The API has no `DELETE` operation on any role path to call and `http.ts` has no helper that could
 * issue one; `vacancyWrites.test.tsx` asserts both, and that no control here matches
 * `/delete|remove/i` with the menu open.
 *
 * ⚠️ **Not rendered for a Recruiter at all — hidden, never disabled** (§5). One predicate,
 * `mayWriteVacancies`, whose docblock cites the two matrix rows it stands on
 * (`post ROLES → (401, 403, 403, 201, 403, 403)` and `patch ROLE → (401, 403, 403, 200, 403, 403)`,
 * identical because both routes declare `write_access = require_group(GROUP_ADMIN)`). A disabled ⋯
 * would tell a Recruiter the capability exists and is being withheld; the whole menu goes instead,
 * which is also why the trigger is not rendered as an empty menu. **The client gate is never the
 * evidence** — the API refuses a Recruiter on its own (the standing obligation of 2026-07-25); what
 * this buys is that nobody is shown a control that would answer 403.
 *
 * ⚠️ **Both items open a dialog and neither writes on selection.** *Edit* is a form and *Close* is a
 * confirmation with something to explain (`CloseVacancyDialog`); a menu item that fired a `PATCH`
 * immediately would also have nowhere to report a 403 or a lost connection.
 */
export function VacancyMenu({ role, groups, viewerId }: Props) {
  const { t } = useTranslation('vacancies');
  const [editing, setEditing] = useState(false);
  const [switchingState, setSwitchingState] = useState(false);
  const [offeringSets, setOfferingSets] = useState(false);

  if (!mayWriteVacancies(groups)) return null;

  /**
   * ⚠️ **An imported vacancy offers neither *Edit* nor *Close*, and both absences are the record's
   * rather than this reader's** (ADR 0020 decision 1, TASK-178). `PATCH /api/v1/roles/{role_id}/`
   * answers **409** on an ADP-sourced row for every caller, an `Admin` included, and closing is a
   * `PATCH` of `is_open` — which on such a row is ADP's projection of its requisition status and not
   * a field anybody here may write. Offering either would be offering the refusal.
   *
   * ⚠️ **The sentence that explains it is on the cell, not in here**, and that is deliberate: a menu
   * closes itself on select and is never open when a reader is wondering why there is nothing to
   * press — the same reason §6.5's refusals stand outside the menu that caused them rather than
   * inside it (`StatusActionAnswer`). `Provenance` in `index.tsx` carries it.
   *
   * ⚠️ **The client is not the enforcement and this is not presented as the protection.** The server
   * refuses on its own (backend TASK-175); what this buys is that nobody presses a control whose only
   * possible answer is a 409.
   */
  const imported = isImportedVacancy(role);

  const items: MenuItem[] = [
    ...(imported ? [] : [{ label: t('menu.edit'), onSelect: () => setEditing(true) }]),
    /*
     * ⚠️ **The one item an imported vacancy keeps**, and that is ADR 0020 decision 1 rather than an
     * oversight: attaching a set is not a field of the requisition, ADP has nothing equivalent, and
     * *"a vacancy that cannot be given questions cannot host an interview, which is the whole reason
     * a vacancy is in Panel"*. `POST /api/v1/roles/{role_id}/question-sets/` and its detach twin
     * answer an imported vacancy exactly as they answer a hand-created one.
     *
     * ⚠️ **The third item, and it is on the same predicate as the other two** (Sprint 20, ADR 0018
     * decision 3). Attaching a set to a vacancy is the **catalogue's** write — `post` and `detach` on
     * `/api/v1/roles/{id}/question-sets/` both declare `write_access = require_group(GROUP_ADMIN)`,
     * the identical row *Edit* and *Close* stand on — so it needs no predicate of its own and a
     * Recruiter sees no `⋯` at all rather than a menu with one item greyed out.
     *
     * It is a **dialog** and not an address: which sets a vacancy offers is a property of the vacancy
     * with no screen of its own, and giving it one would be a second per-vacancy address of exactly
     * the kind ADR 0018 removed.
     */
    { label: t('menu.questionSets'), onSelect: () => setOfferingSets(true) },
    ...(imported
      ? []
      : [
          {
            // One item, and which one it is comes from the vacancy: offering *Close* on a closed
            // vacancy would be a control with nothing to do, and offering both would ask the reader
            // to work out which of them is the current state.
            //
            // ⚠️ Not `destructive` — the `negative` ink of `DropdownMenu`'s destructive item belongs
            // to what cannot be taken back (§6.5's *Anonymize*). Closing is a boolean on a `PATCH`.
            label: role.is_open ? t('menu.close') : t('menu.reopen'),
            onSelect: () => setSwitchingState(true),
          },
        ]),
  ];

  return (
    <>
      <DropdownMenu
        trigger={
          <Button
            variant="ghost"
            size="sm"
            // §4 Icons: an icon is never the only label, and the name carries the **title** because a
            // grid has one of these per cell — *"Actions"* alone would be twelve identical controls
            // to a screen reader moving through the page (§9).
            aria-label={t('menu.label', { title: role.title })}
          >
            <MoreHorizontal size={16} strokeWidth={1.5} aria-hidden />
          </Button>
        }
        items={items}
      />

      {/* Mounted only while open, so each form's defaults are the vacancy as it is now. */}
      {editing && <VacancyDialog vacancy={role} onClose={() => setEditing(false)} />}
      {switchingState && (
        <CloseVacancyDialog vacancy={role} onClose={() => setSwitchingState(false)} />
      )}
      {offeringSets && (
        <VacancySetsDialog
          vacancy={role}
          viewerId={viewerId}
          onClose={() => setOfferingSets(false)}
        />
      )}
    </>
  );
}
